import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { APP_CONFIG } from '@/constants/app';
import { store, clearAuth, showLoader, hideLoader } from '@/store';
import toast from 'react-hot-toast';

class ApiClient {
  private client: AxiosInstance;

  constructor() {
    this.client = axios.create({
      baseURL: APP_CONFIG.API_BASE_URL,
      timeout: 30000,
      headers: {
        'Content-Type': 'application/json',
      },
    });

    this.setupInterceptors();
  }

  private setupInterceptors() {
    this.client.interceptors.request.use(
      (config) => {
        const token = localStorage.getItem(APP_CONFIG.TOKEN_KEY);
        if (token) {
          config.headers.authorization = 'Bearer '+token;
        }

        if (config.showLoader !== false) {
          store.dispatch(showLoader({ backdrop: config.backdrop || false }));
        }

        return config;
      },
      (error) => {
        store.dispatch(hideLoader());
        return Promise.reject(error);
      }
    );

    this.client.interceptors.response.use(
      (response: AxiosResponse) => {
        store.dispatch(hideLoader());
        return response;
      },
      async (error) => {
        store.dispatch(hideLoader());
        
        if (error.response?.status === 401) {
          store.dispatch(clearAuth());
          toast.error('Session expired. Please login again.');
          window.location.href = '/login';
          return Promise.reject(error);
        }

        if (error.response?.status >= 500) {
          // toast.error('Server error. Please try again later.');
             toast.error(error.response.data.message);
        } else if (error.response?.data?.message) {
          toast.error(error.response.data.message);
        } else if (error.message) {
          toast.error(error.message);
        } else {
          toast.error('Something went wrong');
        }

        return Promise.reject(error);
      }
    );
  }

  async get<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.get<T>(url, config);
    return response.data;
  }

  async post<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.post<T>(url, data, config);
    return response.data;
  }

  async put<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.put<T>(url, data, config);
    return response.data;
  }

  async patch<T = any>(url: string, data?: any, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.patch<T>(url, data, config);
    return response.data;
  }

  async delete<T = any>(url: string, config?: AxiosRequestConfig): Promise<T> {
    const response = await this.client.delete<T>(url, config);
    return response.data;
  }

  async upload<T = any>(url: string, file: File, config?: AxiosRequestConfig): Promise<T> {
    const formData = new FormData();
    formData.append('file', file);
    
    const uploadConfig = {
      ...config,
      headers: {
        'Content-Type': 'multipart/form-data',
        ...config?.headers,
      },
    };

    const response = await this.client.post<T>(url, formData, uploadConfig);
    return response.data;
  }

  async download(url: string, filename: string, config?: AxiosRequestConfig): Promise<void> {
    const downloadConfig = {
      ...config,
      responseType: 'blob' as const,
    };

    const response = await this.client.get(url, downloadConfig);
    
    // Create blob link to download
    const blob = new Blob([response.data]);
    const downloadUrl = window.URL.createObjectURL(blob);
    
    // Create temporary link element and trigger download
    const link = document.createElement('a');
    link.href = downloadUrl;
    link.download = filename;
    document.body.appendChild(link);
    link.click();
    
    // Cleanup
    document.body.removeChild(link);
    window.URL.revokeObjectURL(downloadUrl);
  }
}

declare module 'axios' {
  interface AxiosRequestConfig {
    showLoader?: boolean;
    backdrop?: boolean;
  }
}

export const apiClient = new ApiClient();

export const apiGet = apiClient.get.bind(apiClient);
export const apiPost = apiClient.post.bind(apiClient);
export const apiPut = apiClient.put.bind(apiClient);
export const apiPatch = apiClient.patch.bind(apiClient);
export const apiDelete = apiClient.delete.bind(apiClient);
export const apiUpload = apiClient.upload.bind(apiClient);
export const apiDownload = apiClient.download.bind(apiClient);